export enum MediaType {
  Image = 0,
  Video = 1,
}

export enum MediaTypeFilter {
  All = 0,
  Image = 1,
  Video = 2,
}

export enum PersonReviewFilter {
  None = 0,
  NeedsReview = 1,
  NeedsRevision = 2,
  NoPersons = 3,
  HasMarkerOrNoPersons = 4,
}

export enum CollectionFilter {
  Any = 0,
  InCollection = 1,
  NotInCollection = 2,
}

export type AdvancedPersonFilterOperator = 'and' | 'or';
export type AdvancedPersonFilterComparison = 'atLeast' | 'atMost' | 'exact';

export type MediaVisibility = 'private' | 'family';

export interface TagRef {
  id: string;
  name: string;
  color: string;
}

export interface Tag {
  id: string;
  name: string;
  color: string;
  usageCount?: number;
  searchText?: string | null;
  groupId?: string;
  groupName?: string;
  groupPosition: number;
  position: number;
}

export interface TagGroup {
  id: string;
  name: string;
  position: number;
}

export interface MediaItem {
  id: string;
  fileName: string;
  contentType: string;
  mediaType: MediaType;
  title: string;
  description: string;
  capturedAt: string | null;
  isCapturedAtTimeAccurate: boolean;
  hasEmbeddedMetadata: boolean;
  uploadedAt: string;
  widthPx?: number | null;
  heightPx?: number | null;
  durationSeconds?: number | null;
  fileSizeBytes?: number | null;
  fileHash?: string | null;
  createdAt: string;
  lastUpdatedAt: string;
  creator: string;
  lastUpdater: string;
  owner: string;
  ownerDisplayName: string;
  hasPreview: boolean;
  tags: TagRef[];
  protocolNumber?: number | null;
  noPersonsToMark: boolean;
  hasPersonMarkers: boolean;
  isDuplicate?: boolean;
  needsPersonRevision: boolean;
  latitude?: number | null;
  longitude?: number | null;
  visibility: MediaVisibility;
  foldedIntoMediaItemId?: string | null;
  foldedImageCount: number;
  rotationDegrees: number;
}

export interface MediaListResponse {
  mediaItems: MediaItem[];
  totalCount: number;
}

export interface AdvancedPersonFilterCondition {
  personId: string;
  person: Person;
  negated: boolean;
  faceTypes?: FaceType[] | null;
  /** Legacy persisted name, normalized to faceTypes when loaded. */
  faceType?: FaceType | null;
  faceSize?: FaceMarkerSize | null;
  faceSizeComparison?: AdvancedPersonFilterComparison | null;
  generalRating?: number | null;
  generalRatingComparison?: AdvancedPersonFilterComparison | null;
  /** Legacy persisted names, normalized to the fields above when loaded. */
  minimumSize?: FaceMarkerSize | null;
  minimumGeneralRating?: number | null;
}

export interface AdvancedPersonFilter {
  operator: AdvancedPersonFilterOperator;
  conditions: AdvancedPersonFilterCondition[];
}

export interface MediaFoldGroup {
  representative: MediaItem;
  items: MediaItem[];
}

export interface TransferMediaOwnershipResult {
  success: boolean;
  transferredCount: number;
}

export interface ChronologyDay {
  day: number;
  count: number;
}

export interface ChronologyMonth {
  month: number;
  count: number;
  days: ChronologyDay[];
}

export interface ChronologyYear {
  year: number;
  count: number;
  months: ChronologyMonth[];
}

export interface ChronologyTree {
  totalCount: number;
  unknownCount: number;
  years: ChronologyYear[];
}


export interface CreateMediaItemDto {
  fileName: string;
  contentType: string;
  mediaType: MediaType;
  title: string;
  description?: string;
  capturedAt?: string | null;
  tagNames?: string[];
  isCapturedAtTimeAccurate?: boolean;
  protocolNumber?: number | null;
  visibility?: MediaVisibility;
}

export interface UpdateMediaItemDto {
  title: string;
  description: string;
  capturedAt?: string | null;
  tagNames?: string[];
  isCapturedAtTimeAccurate?: boolean;
  noPersonsToMark?: boolean;
  needsPersonRevision?: boolean;
  visibility?: MediaVisibility;
  rotationDegrees?: number;
}

export interface BulkCapturedAtUpdate {
  mediaItemId: string;
  capturedAt: string;
}

export type BulkCaptureTimeUpdateMode = 'date' | 'chronological';

export interface BulkUpdateCapturedAtDto {
  updates: BulkCapturedAtUpdate[];
  mode: BulkCaptureTimeUpdateMode;
  localDate: string;
  localUtcOffsetMinutes: number;
}

export interface BulkUpdateCapturedAtResult {
  success: boolean;
  updatedCount: number;
}

export interface UserSettingEntry {
  key: string;
  value: string;
}

export interface MeResponse {
  username: string;
  displayName?: string;
  permissions: string[];
}

export interface AppUser {
  id: number;
  sub: string;
  username: string;
  email?: string | null;
  displayName?: string | null;
  createdAt: string;
  updatedAt: string;
  isFamilyMember: boolean;
}

export interface TopTagStat {
  tagId: string;
  tagName: string;
  color: string;
  mediaItemCount: number;
}

export interface CapturedByMonthStat {
  label: string;
  year: number;
  month: number;
  count: number;
}

export interface PersonProcessingProgressPoint {
  date: string;
  totalCount: number;
  processedCount: number;
  percentage: number;
}

export interface StatisticsResponse {
  totalMediaItems: number;
  representativeMediaItemCount: number;
  foldedMediaItemCount: number;
  imageCount: number;
  videoCount: number;
  representativeImageCount: number;
  representativeVideoCount: number;
  mediaWithPreviewCount: number;
  totalMediaBytes: number;
  topTags: TopTagStat[];
  capturedByMonth: CapturedByMonthStat[];
  personProcessingProgress: PersonProcessingProgressPoint[];
}

export interface UserStorageStat {
  userId: number;
  username: string;
  displayName: string;
  mediaItemCount: number;
  imageCount: number;
  videoCount: number;
  totalMediaBytes: number;
  totalPreviewBytes: number;
}

export interface MaintenanceInfo {
  totalMediaItems: number;
  imageCount: number;
  videoCount: number;
  totalMediaBytes: number;
  totalPreviewBytes: number;
  orphanedDiskFileCount: number;
  missingMediaFileCount: number;
  userStorage: UserStorageStat[];
  duplicateMediaItemCount: number;
  geoLocationPendingCount: number;
  faceEmbeddingPendingCount: number;
  faceSharpnessPendingCount: number;
  faceSharpnessAutomaticCount: number;
  mediumPreviewPendingCount: number;
  personMarkerPreviewPendingCount: number;
  filenameTitleCount: number;
}

export interface MaintenanceProgressEvent {
  processed: number;
  total: number;
  message: string;
  done: boolean;
  error: boolean;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  deletedCount: number;
}

export interface AppUpdateRelease {
  id: string;
  version: string;
  build: number;
  displayVersion: string;
  fileName: string;
  fileSizeBytes: number;
  uploadedAt: string;
  isCurrent: boolean;
  isPreview: boolean;
  downloadUrl: string;
}

export interface DuplicateMediaItem {
  id: string;
  fileName: string;
  title: string;
  description: string;
  mediaType: number;
  fileSizeBytes: number;
  capturedAt: string | null;
  uploadedAt: string;
  personMarkerCount: number;
  collections: CollectionRef[];
  ownerUsername: string;
  hasPreview: boolean;
}

export interface DuplicateGroup {
  fileHash: string;
  items: DuplicateMediaItem[];
}

export interface DeleteDuplicateMediaItemsResult {
  deletedCount: number;
  errors: string[];
}

export interface ListMediaItemsOptions {
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
  dateFrom?: string;
  dateTo?: string;
  searchText?: string;
  includeDescription?: boolean;
  tagIds?: string[];
  matchAllTags?: boolean;
  mediaType?: MediaTypeFilter;
  protocolNumber?: number;
  onlyUnknownCapturedAt?: boolean;
  personReviewFilter?: PersonReviewFilter;
  personIds?: string[];
  matchAllPersons?: boolean;
  onlyWithoutCollection?: boolean;
  onlyWithCollection?: boolean;
  showFamilyMedia?: boolean;
  /** Keyset-pagination cursor: fetch items strictly after this media item's sort position
   *  instead of using page/pageSize offset math. Stable even if the filtered result set shrinks
   *  or grows between requests (e.g. person-review filters as photos get marked). When set,
   *  `page` is ignored. */
  afterId?: string;
  /** Include folded children instead of returning representatives only. */
  includeFolded?: boolean;
  /** Return only representatives that have at least one folded child. */
  onlyFolded?: boolean;
  hasEmbeddedMetadata?: boolean;
  isCapturedAtTimeAccurate?: boolean;
  advancedPersonFilter?: AdvancedPersonFilter;
}

export interface Rule {
  id: string;
  title: string;
  selector: string;
  code: string;
  creator: string;
  lastUpdater: string;
  createdAt: string;
  updatedAt: string;
  runBeforeMetadataExtraction: boolean;
}

export interface CreateRuleDto {
  title: string;
  selector: string;
  code: string;
  runBeforeMetadataExtraction?: boolean;
}

export interface UpdateRuleDto {
  title: string;
  selector: string;
  code: string;
  runBeforeMetadataExtraction?: boolean;
}

export interface ImportProtocol {
  number: number;
  startedAt: string;
  endedAt?: string | null;
  uploadedByUserName: string;
  source: string;
  totalEntries: number;
  errorEntries: number;
  duplicateEntries: number;
}

export interface ImportProtocolEntry {
  originalFileName: string;
  startedAt: string;
  endedAt?: string | null;
  appliedRuleTitle?: string | null;
  errorMessage?: string | null;
  mediaItemId?: string | null;
  isDuplicate: boolean;
}

export interface ImportProtocolDetail extends ImportProtocol {
  entries: ImportProtocolEntry[];
}

export interface Person {
  id: string;
  firstName: string;
  lastName: string;
  nickName?: string | null;
  birthYear?: number | null;
  birthMonth?: number | null;
  birthDay?: number | null;
  usageCount?: number;
  createdAt?: string;
  lastUpdatedAt?: string;
}

export interface PersonImportMapping {
  id: string;
  sourcePersonId: string;
  targetPersonId: string;
  sourceFirstName: string;
  sourceLastName: string;
  sourceNickName?: string | null;
  targetFirstName: string;
  targetLastName: string;
  targetNickName?: string | null;
  exporterUserName: string;
  importerUserName: string;
  createdAt: string;
  lastUpdatedAt: string;
}

export interface CreatePersonDto {
  firstName: string;
  lastName: string;
  nickName?: string | null;
  birthYear?: number | null;
  birthMonth?: number | null;
  birthDay?: number | null;
}

export interface UpdatePersonDto {
  firstName: string;
  lastName: string;
  nickName?: string | null;
  birthYear?: number | null;
  birthMonth?: number | null;
  birthDay?: number | null;
  clearBirthDate?: boolean;
}

export interface PersonMarker {
  id: string;
  mediaItemId: string;
  personId: string;
  personFirstName: string;
  personLastName: string;
  personNickName?: string | null;
  personBirthYear?: number | null;
  personBirthMonth?: number | null;
  personBirthDay?: number | null;
  x: number;
  y: number;
  width: number;
  height: number;
  sharpnessRating: number;
  sharpnessRatingIsManual: boolean;
  faceType: FaceType;
  generalRating: number;
  lastUpdatedAt: string;
  personMarkerPreviewSavedAt: string | null;
}

export enum FaceType {
  Unrated = 0,
  Smile = 1,
  SadOrCrying = 2,
  Indeterminate = 3,
  BackOfHead = 4,
  Partial = 5,
  Angry = 6,
  Astonished = 7,
}

export enum FaceMarkerSize {
  Micro = 1,
  Mini = 2,
  Small = 3,
  Medium = 4,
  Large = 5,
  Huge = 6,
}

export interface PersonFaceMarker {
  marker: PersonMarker;
  mediaFileName: string;
  mediaTitle: string;
  capturedAt: string | null;
  mediaItem: MediaItem | null;
  position: number;
}

export interface CreatePersonMarkerDto {
  personId: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PersonSuggestion {
  personId: string;
  personFirstName: string;
  personLastName: string;
  personNickName?: string | null;
  score: number;
}

export interface DetectedFace {
  x: number;
  y: number;
  width: number;
  height: number;
  confidence: number;
  suggestions: PersonSuggestion[];
}

export interface UpdatePersonMarkerDto {
  personId?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

export interface UpdatePersonMarkerRatingDto {
  sharpnessRating?: number;
  faceType?: FaceType;
  generalRating?: number;
}

export interface ListPersonFaceMarkersOptions {
  pageSize?: number;
  afterId?: string;
  sortBy?: string;
  sortDescending?: boolean;
  anyUnratedOnly?: boolean;
  sharpnessUnratedOnly?: boolean;
  minSharpnessRating?: number;
  maxSharpnessRating?: number;
  faceType?: FaceType;
  generalRatingUnratedOnly?: boolean;
  minGeneralRating?: number;
  markerSize?: FaceMarkerSize;
}

export interface PersonFaceMarkerListResponse {
  markers: PersonFaceMarker[];
  totalCount: number;
  hasMore: boolean;
}

/** How a marker tooltip should display a person's age at capture time. */
export type AgeDisplayMode = 'off' | 'yearsOnly' | 'yearsAndMonths' | 'bySetting';

/** Which fields to show in a marker's hover tooltip; persisted in localStorage. */
export interface MarkerTooltipFieldOptions {
  name: boolean;
  nickName: boolean;
  ageMode: AgeDisplayMode;
}

export interface CollectionRef {
  id: string;
  title: string;
}

export interface CollectionTreeNode {
  id: string;
  title: string;
  parentId: string | null;
  collectionDate: string | null;
  collectionDateTo: string | null;
  coverMediaItemId: string | null;
  lastUpdatedAt: string;
  subtitle: string;
  coverRotationDegrees?: number;
}

export interface Collection {
  id: string;
  title: string;
  subtitle: string;
  collectionDate: string | null;
  collectionDateTo: string | null;
  parentId: string | null;
  coverMediaItemId: string | null;
  hasCover: boolean;
  childCollectionCount: number;
  mediaItemCount: number;
  createdAt: string;
  lastUpdatedAt: string;
  creator: string;
  lastUpdater: string;
  owner: string;
  ownerDisplayName: string;
  hasLocationMap: boolean;
  locationMapGeneratedAt: string | null;
  visibility: MediaVisibility;
  hideCoverInGallery: boolean;
  coverRotationDegrees?: number;
}

export interface CollectionDetail {
  collection: Collection;
  breadcrumb: CollectionRef[];
  childCollections: Collection[];
  mediaItems: MediaItem[];
  mediaItemsTotalCount: number;
}

export interface CollectionSearchResult {
  collection: Collection;
  breadcrumb: CollectionRef[];
}

export interface CollectionSearchResponse {
  results: CollectionSearchResult[];
  totalCount: number;
}

export interface CollectionExportStatus {
  jobId: string;
  state: 'preparing' | 'creating' | 'ready' | 'failed';
  filesProcessed: number;
  filesTotal: number;
  error: string | null;
}

export interface CollectionImportStatus {
  jobId: string;
  state: 'review' | 'importing' | 'completed' | 'failed';
  collectionsProcessed: number;
  collectionsTotal: number;
  mediaItemsProcessed: number;
  mediaItemsTotal: number;
  markersProcessed: number;
  markersTotal: number;
  error: string | null;
}

export interface ImportedPersonCandidate {
  id: string;
  firstName: string;
  lastName: string;
  nickName: string | null;
  birthYear: number | null;
  birthMonth: number | null;
  birthDay: number | null;
  usageCount: number;
}

export interface ImportedMarkerSample {
  markerId: string;
  mediaItemId: string;
  fileName: string;
  contentType: string;
  mediaType: MediaType;
  widthPx: number | null;
  heightPx: number | null;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImportedPersonInspection {
  id: string;
  firstName: string;
  lastName: string;
  nickName: string | null;
  birthYear: number | null;
  birthMonth: number | null;
  birthDay: number | null;
  sameNameCandidates: ImportedPersonCandidate[];
  knownTargetPersonId: string | null;
  markerSamples: ImportedMarkerSample[];
}

export interface CollectionImportInspection {
  jobId: string;
  format: string;
  formatVersion: number;
  productVersion: string;
  exportedBy: string;
  rootCollectionTitle: string;
  collectionCount: number;
  mediaItemCount: number;
  personMarkerCount: number;
  persons: ImportedPersonInspection[];
  knownMappings: Record<string, string>;
}

export interface CreateCollectionDto {
  title: string;
  subtitle?: string;
  collectionDate?: string | null;
  collectionDateTo?: string | null;
  parentId?: string | null;
  coverMediaItemId?: string | null;
  visibility?: MediaVisibility;
  hideCoverInGallery?: boolean;
}

export interface UpdateCollectionDto {
  title: string;
  subtitle?: string;
  collectionDate?: string | null;
  collectionDateTo?: string | null;
  clearCollectionDate?: boolean;
  clearCollectionDateTo?: boolean;
  coverMediaItemId?: string | null;
  clearCover?: boolean;
  visibility?: MediaVisibility;
  hideCoverInGallery?: boolean;
}

export interface ListCollectionMediaItemsOptions {
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
  collectionSearchText?: string;
  mediaSearchText?: string;
  /** Return only representatives that have at least one folded child. */
  onlyFolded?: boolean;
}
